<script setup>
import {useBaseStore} from "~/stores/base";
import {Navigation, Pagination} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";

const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits(["select", "view-all", "view-more-photos"])

/** props **/
const props = defineProps({
  reviews: {
    default: [],
    required: true
  },
  selectedIndex: {
    default: -1,
    required: false
  },
  btnViewAllVisible: {
    default: true,
    required: false
  },
  btnViewMorePhotosVisible: {
    default: false,
    required: false
  }
})

/** data **/
const swiperInstance = ref(null);

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const slidesPerGroup = computed(() => isMobile.value ? 4 : 3)

/** watch **/
watch(() => props.selectedIndex, async (newIndex) => {
  if (props.selectedIndex > -1) swiperInstance.value.slideTo(newIndex);
})

/** methods **/
const onSwiper = (swiper) => {
  swiperInstance.value = swiper; // 保存 Swiper 实例
};
const onSlideChange = async () => {
};
const handleSelect = (item, index) => {
  emits("select", item.id)
}
/** lifecycle **/

</script>

<template>
  <div class="module-reviews-pic-slides-mobile" v-if="isMobile">
    <div class="title">
      <span>Customer Photos {{ selectedIndex > -1 ? `(${ selectedIndex + 1 }/${ reviews.length })` : '' }}</span>
      <span class="btn" @click="emits('view-more-photos')" v-if="btnViewMorePhotosVisible">View More Photos</span>
      <div class="btn-text" @click="emits('view-all')" v-if="btnViewAllVisible">
        view all
      </div>
    </div>
    <swiper
      v-if="reviews.length"
      grab-cursor
      :slides-per-view="'auto'"
      :slides-per-group="slidesPerGroup"
      :space-between="4"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
    >
      <swiper-slide v-for="(item, index) in reviews" :key="index">
        <img class="multi" src="@/assets/img/icon-multi-image.svg" alt="icon" v-if="item.imageSets.length > 1">
        <img
          class="img"
          :class="{'inactive': selectedIndex !== -1 && selectedIndex !== index}"
          :src="item.imageSets[0].url"
          alt="image"
          @click="handleSelect(item, index)"
        >
      </swiper-slide>
    </swiper>
  </div>
  <div class="module-reviews-pic-slides-desktop" v-else>
    <div class="title">
      <span>Customer Photos {{ selectedIndex > -1 ? `(${ selectedIndex + 1 }/${ reviews.length })` : '' }}</span>
      <span class="btn" @click="emits('view-more-photos')" v-if="btnViewMorePhotosVisible">View More Photos</span>
    </div>
    <swiper
      v-if="reviews.length"
      grab-cursor
      :navigation="{
          prevEl: '.navigation-left',
          nextEl: '.navigation-right'
        }"
      :modules="[Navigation, Pagination]"
      :slides-per-view="'auto'"
      :slides-per-group="slidesPerGroup"
      :space-between="4"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
    >
      <swiper-slide v-for="(item, index) in reviews" :key="index">
        <img class="multi" src="@/assets/img/icon-multi-image.svg" alt="icon" v-if="item.imageSets.length > 1">
        <img
          class="img"
          :class="{'inactive': selectedIndex !== -1 && selectedIndex !== index}"
          :src="item.imageSets[0].url"
          alt="image"
          @click="handleSelect(item, index)"
        >
      </swiper-slide>

      <!-- If we need navigation buttons -->
      <div class="navigation-left" v-if="reviews.length > 3">
        <img src="@/assets/img/icon-left-small.svg" alt="left">
      </div>
      <div class="navigation-right" v-if="reviews.length > 3">
        <img src="@/assets/img/icon-right-small.svg" alt="right">
      </div>
    </swiper>
    <div class="btn-text" @click="emits('view-all')" v-if="btnViewAllVisible">
      view all
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-reviews-pic-slides-mobile {
  background-color: white;
  padding: 10px 16px;

  .title {
    font-family: "TWK Lausanne";
    font-size: 12px;
    font-style: normal;
    font-weight: 650;
    line-height: 18px; /* 150% */
    letter-spacing: -0.048px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .btn {
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 150% */
      letter-spacing: -0.048px;
      text-decoration: underline;
      cursor: pointer;
    }

    .btn-text {
      padding: 0;
    }
  }

  .swiper {
    margin-top: 10px;

    .swiper-slide {
      width: fit-content;
      position: relative;
      display: flex;

      .multi {
        position: absolute;
        right: 5px;
        top: 5px;
      }

      .img {
        cursor: pointer;

        width: 80px;
        height: 80px;
        object-fit: cover;
        object-position: center center;
        transition: all .1s linear;
      }

      .inactive {
        opacity: 0.3;
      }
    }
  }
}

.module-reviews-pic-slides-desktop {
  background-color: white;
  padding-bottom: 15px;

  .title {
    font-family: "TWK Lausanne";
    font-size: 12px;
    font-style: normal;
    font-weight: 650;
    line-height: 18px; /* 150% */
    letter-spacing: -0.048px;
    padding: 0 16px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .btn {
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 150% */
      letter-spacing: -0.048px;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .swiper {
    padding: 10px 0;

    .swiper-slide {
      width: fit-content;
      position: relative;
      display: flex;

      .multi {
        position: absolute;
        right: 5px;
        top: 5px;
      }

      .img {
        cursor: pointer;

        width: 100px;
        height: 100px;
        object-fit: cover;
        object-position: center center;
        transition: all .1s linear;
      }

      .inactive {
        opacity: 0.3;
      }
    }

    .navigation-left {
      position: absolute;
      display: flex;
      left: 0;
      top: 50%;
      z-index: 1;
      transform: translate(0, -50%);
      cursor: pointer;

      img {
        width: 30px;
        height: 30px;
      }
    }

    .navigation-right {
      position: absolute;
      display: flex;
      right: 0;
      top: 50%;
      z-index: 1;
      transform: translate(0, -50%);
      cursor: pointer;

      img {
        width: 30px;
        height: 30px;
      }
    }
  }

  .btn-text {
    padding: 0;
  }
}
</style>
